/**
 * RACE SUMMARY course insight panels: wind strip, finish-gap drivers, course view + DTL chart,
 * leg-by-leg. Data from race_timeline (0_race_fleet.py) and race-summary rows.
 * Course marks come from markwind (same series the fleet map animates) when available.
 */
import { createEffect, createMemo, createSignal, For, onCleanup, Show } from "solid-js";
import { persistantStore } from "../../../store/persistantStore";
import SectionHead from "./SectionHead";
import RaceDriversPanel from "./RaceDriversPanel";
import LegDriversPanel from "./LegDriversPanel";
import RaceCourseView, { type CourseColorMode } from "./RaceCourseView";
import DtlChart from "./DtlChart";
import {
  dayDriversView,
  dayLegDriversView,
  legDriversView,
  raceDriversView,
} from "./raceDrivers";
import {
  fetchDayMarkCourse,
  markCourseChangesForRace,
  timelineWindKph,
  type MarkwindCourseSnapshot,
  type RaceTimelineRace,
  type RaceTimelineMark,
} from "./raceTimeline";
import { speedUnitShortLabel, type SpeedDisplayUnit } from "../../../utils/speedUnits";
import { error as logError } from "../../../utils/console";

interface Props {
  /** Selected race key ("All" or a race number string). */
  selectedRace: string;
  /** Selected leg ("All" or a leg number string). */
  selectedLeg: string;
  /** race_timeline races keyed by race number. */
  timeline: Record<string, RaceTimelineRace>;
  /** Source names currently shown (filtered). */
  sources: string[];
  /** Speed unit for wind strip labels. */
  speedUnit: SpeedDisplayUnit;
  /** Format a speed magnitude already in kph for display. */
  formatSpeedKph: (kph: number | null | undefined) => string;
  /** Optional course-axis deg / source from race-summary rows. */
  courseAxisDeg?: number | null;
  courseAxisSource?: string | null;
}

export default function RaceSummaryCoursePanels(props: Props) {
  const [cursor, setCursor] = createSignal<number | null>(null);
  const [colorMode, setColorMode] = createSignal<CourseColorMode>("boat");
  const [markSnaps, setMarkSnaps] = createSignal<MarkwindCourseSnapshot[]>([]);

  createEffect(() => {
    const className = persistantStore.selectedClassName();
    const projectId = persistantStore.selectedProjectId();
    const date = persistantStore.selectedDate();
    if (!className || !projectId || !date) {
      setMarkSnaps([]);
      return;
    }
    const ctrl = new AbortController();
    fetchDayMarkCourse(className, projectId, String(date), ctrl.signal)
      .then((snaps) => {
        if (!ctrl.signal.aborted) setMarkSnaps(snaps);
      })
      .catch((err) => logError("RaceSummaryCoursePanels: markwind load failed", err));
    onCleanup(() => ctrl.abort());
  });

  const legNum = createMemo(() => {
    const leg = props.selectedLeg;
    if (!leg || leg === "All") return null;
    const n = parseInt(leg, 10);
    return Number.isFinite(n) && n >= 1 ? n : null;
  });

  const selectedTimelineRace = createMemo((): RaceTimelineRace | null => {
    if (props.selectedRace === "All") return null;
    return props.timeline[String(props.selectedRace)] ?? null;
  });

  const driversView = createMemo(() => {
    const sources = props.sources;
    const leg = legNum();
    if (props.selectedRace === "All") {
      const races = Object.values(props.timeline);
      return leg != null ? dayLegDriversView(races, leg, sources) : dayDriversView(races, sources);
    }
    const t = selectedTimelineRace();
    if (!t) return null;
    return leg != null ? legDriversView(t, leg, sources) : raceDriversView(t, sources);
  });

  const windSummary = createMemo(() => {
    const races =
      props.selectedRace === "All"
        ? Object.values(props.timeline)
        : [selectedTimelineRace()].filter(Boolean) as RaceTimelineRace[];
    const fromTimeline = timelineWindKph(races);
    return {
      tws: fromTimeline?.tws ?? null,
      twsMin: fromTimeline?.twsMin ?? null,
      twsMax: fromTimeline?.twsMax ?? null,
      twd: fromTimeline?.twd ?? null,
      shift: fromTimeline?.shift ?? null,
      source: fromTimeline?.source ?? null,
      axis: props.courseAxisDeg ?? null,
      axisSource: props.courseAxisSource ?? null,
    };
  });

  const legEntries = createMemo(() => {
    const t = selectedTimelineRace();
    const drivers = t?.drivers;
    if (!drivers?.legs?.length) return [];
    return drivers.legs;
  });

  /** Markwind timesteps for the selected race, as course_changes (t seconds from t0). */
  const markCourseChanges = createMemo((): { t: number; marks: RaceTimelineMark[] }[] => {
    const race = selectedTimelineRace();
    const snaps = markSnaps();
    if (!race || !snaps.length || race.t0 == null) return [];
    const durationS = (race.n || 0) * (race.dt || 1);
    return markCourseChangesForRace(snaps, race.t0, durationS);
  });

  return (
    <div class="race-summary-course-panels">
      <Show when={windSummary().tws != null || windSummary().axis != null}>
        <SectionHead title="Wind" />
        <div class="race-wind-strip">
          <div class="race-wind-tile">
            <span class="race-wind-tile-label">TWS {speedUnitShortLabel(props.speedUnit)}</span>
            <span class="race-wind-tile-value">{props.formatSpeedKph(windSummary().tws)}</span>
            <span class="race-wind-tile-sub">
              {windSummary().twsMin != null && windSummary().twsMax != null
                ? `range ${props.formatSpeedKph(windSummary().twsMin)} – ${props.formatSpeedKph(windSummary().twsMax)}`
                : "range not computed"}
              {windSummary().source === "marks" ? " · race marks" : ""}
            </span>
          </div>
          <div class="race-wind-tile">
            <span class="race-wind-tile-label">TWD</span>
            <span class="race-wind-tile-value">
              {windSummary().twd == null ? "—" : `${Math.round(windSummary().twd as number)}°`}
            </span>
            <span class="race-wind-tile-sub">wind from</span>
          </div>
          <div class="race-wind-tile">
            <span class="race-wind-tile-label">SHIFT RANGE</span>
            <span class="race-wind-tile-value">
              {windSummary().shift == null ? "—" : `${Math.round(windSummary().shift as number)}°`}
            </span>
            <span class="race-wind-tile-sub">TWD 5th–95th pct</span>
          </div>
          <div class="race-wind-tile">
            <span class="race-wind-tile-label">COURSE AXIS</span>
            <span class="race-wind-tile-value">
              {windSummary().axis == null ? "—" : `${Math.round(windSummary().axis as number)}°`}
            </span>
            <span class="race-wind-tile-sub">
              {windSummary().axisSource ? `from ${windSummary().axisSource}` : "not computed"}
            </span>
          </div>
        </div>
      </Show>

      <Show when={driversView()}>
        {(view) => (
          <div class="race-summary-drivers-block">
            <SectionHead
              title={
                legNum() != null
                  ? `Leg ${legNum()} gap breakdown`
                  : props.selectedRace === "All"
                    ? "Finish gap breakdown (day average)"
                    : "Finish gap breakdown"
              }
            />
            <RaceDriversPanel
              view={view()}
              race={props.selectedRace === "All" ? null : Number(props.selectedRace)}
              leg={legNum()}
            />
          </div>
        )}
      </Show>

      <Show when={selectedTimelineRace()}>
        {(race) => (
          <div class="race-visuals-section">
            <SectionHead
              title={
                legNum() != null
                  ? `Race ${race().race}, leg ${legNum()}: course and distance lost to the leader`
                  : `Race ${race().race}: course and distance to leader`
              }
            />
            <div class="race-color-toggle">
              <span class="race-color-toggle-label">Colour</span>
              <For each={(["boat", "wind", "dtl"] as CourseColorMode[])}>
                {(mode) => (
                  <button
                    type="button"
                    class={`race-color-toggle-btn${colorMode() === mode ? " race-color-toggle-btn-active" : ""}`}
                    onClick={() => setColorMode(mode)}
                  >
                    {mode === "boat" ? "Boat" : mode === "wind" ? "Wind" : "DTL Δ"}
                  </button>
                )}
              </For>
            </div>
            <div class="race-visuals-grid">
              <RaceCourseView
                race={race()}
                sources={props.sources}
                mode={colorMode()}
                cursor={cursor()}
                leg={legNum()}
                speedUnit={props.speedUnit}
                markCourseChanges={markCourseChanges()}
              />
              <DtlChart
                race={race()}
                sources={props.sources}
                cursor={cursor()}
                onCursor={setCursor}
                leg={legNum()}
              />
            </div>
            <Show when={legEntries().length > 0 && legNum() == null}>
              <LegDriversPanel legs={legEntries()} sources={props.sources} />
            </Show>
          </div>
        )}
      </Show>

      <Show when={props.selectedRace !== "All" && !selectedTimelineRace()}>
        <p class="race-summary-empty-message">
          No race_timeline for this race yet. Run Admin → Race stats: fleet after every boat's Race.py.
        </p>
      </Show>
    </div>
  );
}
